import { hashString, StoredProcedureScope, WorkflowAsTasks, WorkflowVariable } from '@cloud-native/workflows-engine'
import { formatQueryCall } from 'features/workflows/utils/formatCodeSnippets'
import { formatQueryParams } from 'features/sql/utils'
import { API_COMPONENT, MCP_TOOL_COMPONENT } from 'features/workflows/constants'
import { Workflow } from 'features/workflows/types'
import { WorkflowProcedureStatus } from 'features/workflows/enums'
import { getApiVariables, getMcpToolVariables } from 'features/workflows/utils/variables'

export type Procedure = NonNullable<Workflow['config']['procedure']>
export type McpToolProcedure = NonNullable<Workflow['config']['mcpTool']['procedure']>

type UnifiedProcedure = Procedure | McpToolProcedure

type ScopeSubset = StoredProcedureScope.Api | StoredProcedureScope.McpTool

export const SCOPE_CONFIG_ACCESSORS: Record<
  ScopeSubset,
  {
    get: (cfg: Workflow['config']) => UnifiedProcedure | null | undefined
    set: (cfg: Workflow['config'], proc: UnifiedProcedure | null) => void
  }
> = {
  [StoredProcedureScope.Api]: {
    get: (cfg) => cfg.procedure,
    set: (cfg, proc) => {
      cfg.procedure = proc as Procedure | null
    }
  },
  [StoredProcedureScope.McpTool]: {
    get: (cfg) => cfg.mcpTool.procedure,
    set: (cfg, proc) => {
      cfg.mcpTool ??= {
        id: '',
        enabled: false,
        name: '',
        description: '',
        inputs: [],
        output: {
          description: '',
          method: 'GET' as const,
          endpoint: '',
          payload: {
            q: '',
            query: '',
            queryParameters: []
          }
        },
        procedure: null,
        draft: {
          description: '',
          inputs: {},
          output: { description: '' }
        }
      }
      cfg.mcpTool.procedure = proc as McpToolProcedure | null
    }
  }
}

export const SCOPE_VARIABLES_ACCESSORS: Record<
  ScopeSubset,
  (vars: WorkflowVariable[] | undefined) => WorkflowVariable[]
> = {
  [StoredProcedureScope.Api]: (vars) => getApiVariables(vars),
  [StoredProcedureScope.McpTool]: (vars) => getMcpToolVariables(vars)
}

export function isProcedureOutdated(workflow: Workflow, scope: StoredProcedureScope) {
  const status = SCOPE_CONFIG_ACCESSORS[scope].get(workflow.config)?.status
  return status === WorkflowProcedureStatus.Outdated
}

export function isProcedureUpdated(workflow: Workflow, scope: StoredProcedureScope) {
  const status = SCOPE_CONFIG_ACCESSORS[scope].get(workflow.config)?.status
  return status === WorkflowProcedureStatus.Updated
}

export function cleanComments(query: string | undefined) {
  return query
    ?.replace(/\/\*[\s\S]*?\*\//g, '') // Remove /* */ comments
    ?.replace(/\s+/g, ' ') // Normalize whitespace
    ?.trim()
}

export function compareQueries(queryA: string | undefined, queryB: string | undefined) {
  return cleanComments(queryA) === cleanComments(queryB)
}

export function generateCall(query: string, workflow: Workflow, scope: StoredProcedureScope) {
  const variables = SCOPE_VARIABLES_ACCESSORS[scope](workflow.config.variables)
  const name = query.match(/CREATE OR REPLACE PROCEDURE\s*([^(]+)\(/)?.[1] || ''
  const provider = workflow.config.connectionProvider
  const targetComponent = {
    [StoredProcedureScope.Api]: API_COMPONENT,
    [StoredProcedureScope.McpTool]: MCP_TOOL_COMPONENT
  }[scope]
  const hasOutputNode = Boolean(targetComponent && workflow.config.nodes.find((n) => n.data.name === targetComponent))
  return formatQueryCall({ name, variables, provider, hasOutputNode })
}

export function generateParams(workflow: Workflow, scope: StoredProcedureScope) {
  const variables = SCOPE_VARIABLES_ACCESSORS[scope](workflow.config.variables)
  const provider = workflow?.config.connectionProvider
  return JSON.stringify(formatQueryParams({ variables, provider }))
}

export function generateQueryHash(query: string) {
  // Remove comments before hashing
  const cleanQuery = cleanComments(query)
  return hashString(cleanQuery || '')
}

export function generateTasksListHash(tasks: WorkflowAsTasks | null) {
  return hashString(JSON.stringify(tasks) || '')
}
